import React from "react";
import { Link } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineSearch,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineBell,
    HiPlus
} from "react-icons/hi";

const BrandDashboard = () => {
    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex">
                <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                    <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                    <span>Braccet</span>
                </div>

                <nav className="flex-1 px-4 py-6 space-y-2">
                    <NavItem to="/brand/dashboard" icon={<HiOutlineHome />} label="Overview" active />
                    <NavItem to="/brand/campaigns" icon={<HiOutlineBriefcase />} label="Campaigns" />
                    <NavItem to="/brand/search" icon={<HiOutlineSearch />} label="Find Creators" />
                    <NavItem to="/brand/messages" icon={<HiOutlineChatAlt2 />} label="Messages" />
                    <NavItem to="/brand/settings" icon={<HiOutlineCog />} label="Settings" />
                </nav>

                <div className="p-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 cursor-pointer">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            B
                        </div>
                        <div>
                            <p className="text-sm font-bold text-gray-900">Brand Name</p>
                            <p className="text-xs text-gray-500">brand@example.com</p>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex flex-col h-screen">
                <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 py-3 flex justify-between items-center shrink-0">
                    <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                    <div className="flex items-center gap-4">
                        <button className="p-2 text-gray-400 hover:text-gray-600 relative">
                            <HiOutlineBell className="text-xl" />
                            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
                        </button>
                        <button
                            className="bg-[#0044CC] text-white px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 hover:bg-blue-700 transition-colors"
                            onClick={() => window.location.href = '/brand/create-campaign'}
                        >
                            <HiPlus className="text-lg" />
                            Create Campaign
                        </button>
                    </div>
                </header>

                <div className="p-4 w-full mx-auto space-y-4 flex-1 h-full flex flex-col overflow-hidden box-border">
                    {/* Stats Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <StatCard
                            title="Active Campaigns"
                            value="3"
                            change="+1 this month"
                            icon={<HiOutlineBriefcase className="text-blue-600" />}
                            bg="bg-blue-50"
                        />
                        <StatCard
                            title="Pending Applications"
                            value="12"
                            change="5 new today"
                            icon={<HiOutlineSearch className="text-purple-600" />}
                            bg="bg-purple-50"
                        />
                        <StatCard
                            title="Total Spend"
                            value="$2,450"
                            change="+12% vs last month"
                            icon={<HiOutlineChatAlt2 className="text-green-600" />}
                            bg="bg-green-50"
                        />
                    </div>

                    {/* Recent Activity Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1 min-h-0 pb-4">
                        {/* Main Chart/Table Area */}
                        <div className="lg:col-span-2 flex flex-col min-h-0">
                            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col h-full overflow-hidden">
                                <div className="flex justify-between items-center mb-4 shrink-0">
                                    <h2 className="text-lg font-bold text-gray-900">Recent Applications</h2>
                                    <Link to="/brand/applications" className="text-sm text-blue-600 font-medium hover:underline">View All</Link>
                                </div>
                                <div className="space-y-2 overflow-y-auto pr-2 custom-scrollbar flex-1">
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors border border-gray-50 gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden shrink-0">
                                                    <img src={`https://i.pravatar.cc/150?u=${i}`} alt="Creator" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">Sarah Jenkins</p>
                                                    <p className="text-xs text-gray-500">Applied for "Summer Glow..."</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-gray-300">Profile</button>
                                                <button className="px-3 py-1.5 text-xs font-medium text-white bg-black rounded-lg hover:bg-gray-800">Review</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Recent Messages / Notifications */}
                        <div className="lg:col-span-1 flex flex-col min-h-0">
                            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col h-full overflow-hidden">
                                <h2 className="text-lg font-bold text-gray-900 mb-4 shrink-0">Suggested Creators</h2>
                                <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-1">
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <div key={i} className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden shrink-0">
                                                <img src={`https://i.pravatar.cc/150?u=${i + 10}`} alt="Creator" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold text-gray-900 truncate">Alex Rivera</p>
                                                <p className="text-xs text-gray-500 truncate">Beauty & Lifestyle • 45k</p>
                                            </div>
                                            <button className="text-blue-600 text-xs font-bold hover:underline shrink-0">View</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

const NavItem = ({ to, icon, label, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

const StatCard = ({ title, value, change, icon, bg }) => (
    <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-start justify-between">
        <div>
            <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
            <p className="text-xs text-green-600 font-medium">{change}</p>
        </div>
        <div className={`p-3 rounded-xl ${bg}`}>
            {icon}
        </div>
    </div>
);

export default BrandDashboard;
